import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Star } from 'lucide-react';
import type { UserFavoriteWithArche } from '@/lib/api';
import { FavoriteCard, FavoriteIcon } from './FavoriteCard';

/** Fixed column count for consistent grid placement */
const GRID_COLS = 5;
/** Minimum rows always visible */
const MIN_ROWS = 7;
/** Extra empty rows below the last occupied cell */
const EXTRA_ROWS = 2;

interface FavoritesGridProps {
  favorites: UserFavoriteWithArche[];
  onReorder: (placements: { archId: string; position: number }[]) => void;
}

export function FavoritesGrid({ favorites, onReorder }: FavoritesGridProps) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Sorted for consistent ordering
  const sortedFavorites = [...favorites].sort((a, b) => a.position - b.position);
  const sortableIds = sortedFavorites.map((f) => f.archId);

  // Position → favorite lookup
  const positionMap = new Map<number, UserFavoriteWithArche>();
  for (const fav of favorites) {
    positionMap.set(fav.position, fav);
  }

  // Grid size
  const maxPosition = favorites.length > 0 ? Math.max(...favorites.map((f) => f.position)) : -1;
  const minCells = MIN_ROWS * GRID_COLS;
  const cellsNeeded = maxPosition + 1 + EXTRA_ROWS * GRID_COLS;
  const cellCount = Math.ceil(Math.max(minCells, cellsNeeded) / GRID_COLS) * GRID_COLS;

  const activeFavorite = activeDragId ? favorites.find((f) => f.archId === activeDragId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const draggedArchId = active.id as string;
    const overId = String(over.id);

    if (overId.startsWith('empty_')) {
      // --- Placement on empty cell ---
      const targetPosition = parseInt(overId.replace('empty_', ''), 10);
      const placements = favorites.map((f) => ({
        archId: f.archId,
        position: f.archId === draggedArchId ? targetPosition : f.position,
      }));
      onReorder(placements);
    } else {
      // --- Shift among sortable items ---
      const oldIndex = sortableIds.indexOf(draggedArchId);
      const newIndex = sortableIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(sortableIds, oldIndex, newIndex);
      // Items take the positions of the sorted slots they now occupy
      const sortedPositions = sortedFavorites.map((f) => f.position);
      const placements = newOrder.map((archId, i) => ({
        archId,
        position: sortedPositions[i],
      }));
      onReorder(placements);
    }
  };

  if (favorites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Star className="h-12 w-12 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Your worktop is empty</h3>
        <p className="text-muted-foreground max-w-md">
          Click the star icon on any arche to add it to your worktop for quick access.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-4 lg:grid-cols-5 gap-x-3 sm:gap-x-6 gap-y-4 max-w-xl mx-auto">
          {Array.from({ length: cellCount }, (_, cellIndex) => {
            const fav = positionMap.get(cellIndex);
            if (fav) {
              return <FavoriteCard key={fav.archId} favorite={fav} />;
            }
            return (
              <EmptyCell
                key={`empty_${cellIndex}`}
                id={`empty_${cellIndex}`}
                isDragActive={activeDragId !== null}
              />
            );
          })}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeFavorite ? (
          <div className="w-20 flex flex-col items-center">
            <FavoriteIcon favorite={activeFavorite} />
            <span className="mt-2 text-xs font-medium text-foreground text-center line-clamp-2 w-full px-1">
              {activeFavorite.archeName}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// =============================================================================
// Empty grid cell — standalone droppable (not sortable)
// =============================================================================

function EmptyCell({ id, isDragActive }: { id: string; isDragActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[5rem] rounded-2xl transition-colors ${
        isDragActive ? 'border-2 border-dashed border-border/30' : ''
      } ${isOver ? 'bg-primary/10 border-primary/40!' : ''}`}
    />
  );
}
